import type {
  ProjectDrawingOfflineArtifact,
  ProjectDrawingOfflineDelivery,
} from '../types';

// Low-level IndexedDB + OPFS + integrity-check primitives shared between the
// main thread (drawingOfflineStore.ts, for reads/listing/cleanup) and the
// download worker (drawingOfflineWorker.ts, for the actual chunked download
// loop). Single source of truth so the two never drift apart — see the
// Drawings stabilization plan's Phase 1c.

export const OFFLINE_DB_NAME = 'forxa-drawing-offline';
export const OFFLINE_DB_VERSION = 1;
export const ARTIFACT_STORE = 'artifacts';
export const BOOTSTRAP_STORE = 'viewer-bootstraps';
export const OPFS_ROOT = 'forxa-drawings-v1';
export const DELIVERY_REFRESH_ATTEMPTS = 2;

export type DrawingOfflineDownloadState =
  | 'queued'
  | 'downloading'
  | 'paused'
  | 'available'
  | 'failed';

export interface LocalDrawingOfflineRecord {
  key: string;
  userId: number;
  artifactId: string;
  artifact: ProjectDrawingOfflineArtifact;
  state: DrawingOfflineDownloadState;
  committedBytes: number;
  fileName: string;
  accessExpiresAt: string;
  createdAt: number;
  updatedAt: number;
  lastOpenedAt?: number;
  keepOffline?: boolean;
  error?: string | null;
}

export interface DrawingOfflineProgress {
  artifactId: string;
  state: DrawingOfflineDownloadState;
  downloadedBytes: number;
  totalBytes: number;
}

let databasePromise: Promise<IDBDatabase> | null = null;

export function artifactKey(userId: number, artifactId: string): string {
  return `${userId}:${artifactId}`;
}

export function requestResult<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error || new Error('Offline drawing storage request failed'));
  });
}

export function transactionComplete(transaction: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onabort = () => reject(transaction.error || new Error('Offline drawing transaction was aborted'));
    transaction.onerror = () => reject(transaction.error || new Error('Offline drawing transaction failed'));
  });
}

export function assertBrowserStorage(): void {
  if (typeof indexedDB === 'undefined' || !navigator.storage?.getDirectory) {
    throw new Error('This browser does not support managed offline drawings');
  }
}

// Only meaningful in whichever execution context calls it — the main thread
// and the download worker each instantiate this module (and its module-level
// databasePromise) independently, since a Worker has its own module graph.
export function resetDatabaseConnection(): void {
  databasePromise = null;
}

export function openDatabase(): Promise<IDBDatabase> {
  assertBrowserStorage();
  if (databasePromise) return databasePromise;
  databasePromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(OFFLINE_DB_NAME, OFFLINE_DB_VERSION);
    request.onupgradeneeded = () => {
      const database = request.result;
      if (!database.objectStoreNames.contains(ARTIFACT_STORE)) {
        const artifacts = database.createObjectStore(ARTIFACT_STORE, { keyPath: 'key' });
        artifacts.createIndex('userId', 'userId', { unique: false });
        artifacts.createIndex('projectId', 'artifact.project_id', { unique: false });
        artifacts.createIndex('revisionId', 'artifact.drawing_revision_id', { unique: false });
      }
      if (!database.objectStoreNames.contains(BOOTSTRAP_STORE)) {
        const bootstraps = database.createObjectStore(BOOTSTRAP_STORE, { keyPath: 'key' });
        bootstraps.createIndex('userId', 'userId', { unique: false });
      }
    };
    request.onsuccess = () => {
      request.result.onversionchange = () => {
        request.result.close();
        databasePromise = null;
      };
      resolve(request.result);
    };
    request.onerror = () => {
      databasePromise = null;
      reject(request.error || new Error('Offline drawing storage is unavailable'));
    };
    request.onblocked = () => {
      databasePromise = null;
      reject(new Error('Offline drawing storage upgrade is blocked by another tab'));
    };
  });
  return databasePromise;
}

export function safeArtifactFileName(artifactId: string, checksum: string): string {
  const safeId = artifactId.replace(/[^a-zA-Z0-9._-]/g, '_').slice(0, 140);
  return `${safeId}-${checksum.slice(0, 16)}.pdf`;
}

export async function getUserDirectory(userId: number, create = true): Promise<FileSystemDirectoryHandle> {
  assertBrowserStorage();
  const storageRoot = await navigator.storage.getDirectory();
  const drawingsRoot = await storageRoot.getDirectoryHandle(OPFS_ROOT, { create });
  return drawingsRoot.getDirectoryHandle(`user-${userId}`, { create });
}

export async function getFileHandle(record: LocalDrawingOfflineRecord, create = true): Promise<FileSystemFileHandle> {
  const directory = await getUserDirectory(record.userId, create);
  return directory.getFileHandle(record.fileName, { create });
}

export async function getArtifactRecordByKey(key: string): Promise<LocalDrawingOfflineRecord | null> {
  const database = await openDatabase();
  const transaction = database.transaction(ARTIFACT_STORE, 'readonly');
  const result = await requestResult(transaction.objectStore(ARTIFACT_STORE).get(key)) as LocalDrawingOfflineRecord | undefined;
  await transactionComplete(transaction);
  return result || null;
}

export async function putArtifactRecord(record: LocalDrawingOfflineRecord): Promise<void> {
  const database = await openDatabase();
  const transaction = database.transaction(ARTIFACT_STORE, 'readwrite');
  transaction.objectStore(ARTIFACT_STORE).put(record);
  await transactionComplete(transaction);
}

export async function deleteArtifactRecord(key: string): Promise<void> {
  const database = await openDatabase();
  const transaction = database.transaction(ARTIFACT_STORE, 'readwrite');
  transaction.objectStore(ARTIFACT_STORE).delete(key);
  await transactionComplete(transaction);
}

export async function removeOfflineDrawingEntry(userId: number, artifactId: string): Promise<void> {
  const key = artifactKey(userId, artifactId);
  const record = await getArtifactRecordByKey(key);
  if (!record) return;
  const directory = await getUserDirectory(userId, false).catch(() => null);
  if (directory) await directory.removeEntry(record.fileName).catch(() => {});
  await deleteArtifactRecord(key);
}

export function normalizeSha256(value: string): string {
  return value.trim().toLowerCase();
}

export async function sha256Hex(bytes: ArrayBuffer): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', bytes);
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('');
}

export function reportProgress(
  record: LocalDrawingOfflineRecord,
  onProgress?: (progress: DrawingOfflineProgress) => void,
): void {
  onProgress?.({
    artifactId: record.artifactId,
    state: record.state,
    downloadedBytes: record.committedBytes,
    totalBytes: record.artifact.file_size,
  });
}

export async function fetchRange(
  delivery: ProjectDrawingOfflineDelivery,
  start: number,
  end: number,
  signal?: AbortSignal,
): Promise<{ bytes: ArrayBuffer; fullResponse: boolean }> {
  if (delivery.mode !== 'direct') {
    throw new Error('Offline downloads require a direct temporary file link');
  }
  const response = await fetch(delivery.url, {
    method: 'GET',
    credentials: 'omit',
    signal,
    headers: {
      Accept: 'application/pdf',
      Range: `bytes=${start}-${end}`,
    },
  });
  if (response.status === 401 || response.status === 403 || response.status === 410) {
    throw Object.assign(new Error('The temporary drawing link expired'), { refreshDelivery: true });
  }
  if (response.status !== 206 && response.status !== 200) {
    throw new Error(`Could not download drawing PDF range (${response.status})`);
  }
  const bytes = await response.arrayBuffer();
  return { bytes, fullResponse: response.status === 200 };
}

export async function resetPartialFile(record: LocalDrawingOfflineRecord): Promise<LocalDrawingOfflineRecord> {
  const handle = await getFileHandle(record);
  const writable = await handle.createWritable();
  await writable.truncate(0);
  await writable.close();
  const reset = { ...record, committedBytes: 0, updatedAt: Date.now(), error: null };
  await putArtifactRecord(reset);
  return reset;
}

export async function verifyAndWriteFullResponse(
  record: LocalDrawingOfflineRecord,
  bytes: ArrayBuffer,
): Promise<LocalDrawingOfflineRecord> {
  const artifact = record.artifact;
  if (bytes.byteLength !== artifact.file_size) {
    throw new Error('The offline drawing download was incomplete');
  }
  const chunkSize = artifact.integrity_chunk_size as number;
  for (let start = 0, index = 0; start < bytes.byteLength; start += chunkSize, index += 1) {
    const end = Math.min(bytes.byteLength, start + chunkSize);
    const hash = await sha256Hex(bytes.slice(start, end));
    if (hash !== normalizeSha256(artifact.integrity_chunk_sha256[index])) {
      throw new Error(`Drawing integrity check failed at block ${index + 1}`);
    }
  }
  const finalHash = await sha256Hex(bytes);
  if (finalHash !== normalizeSha256(artifact.checksum_sha256 as string)) {
    throw new Error('The downloaded drawing did not match its immutable source');
  }
  const handle = await getFileHandle(record);
  const writable = await handle.createWritable();
  await writable.write(bytes);
  await writable.truncate(bytes.byteLength);
  await writable.close();
  return { ...record, committedBytes: bytes.byteLength };
}

export function validateArtifact(artifact: ProjectDrawingOfflineArtifact): asserts artifact is ProjectDrawingOfflineArtifact & {
  artifact_id: string;
  checksum_sha256: string;
  integrity_chunk_size: number;
} {
  if (artifact.availability !== 'ready' || !artifact.artifact_id) {
    throw new Error('This drawing is still preparing its offline file');
  }
  if (!artifact.checksum_sha256 || !artifact.integrity_chunk_size || !artifact.integrity_chunk_sha256.length) {
    throw new Error('This drawing does not have a complete integrity manifest yet');
  }
  const expectedChunks = Math.ceil(artifact.file_size / artifact.integrity_chunk_size);
  if (expectedChunks !== artifact.integrity_chunk_sha256.length) {
    throw new Error('The drawing integrity manifest is incomplete');
  }
}
